import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User } from "lucide-react";
import { useLazyGetConversationsQuery } from "@/store/rest_api";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { shortTimeAgo } from "@/util/date";
import { getDisplayName, getConversationInitials } from "@/components/pages/messages/ConversationListItem";
import { useMessageDialog } from "@/context/MessageDialogContext";
import Conversation from "@/model/interface/Conversation";

const PAGE_SIZE = 20;

export default function ConversationsList() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [fetchConversations, { isLoading }] = useLazyGetConversationsQuery();
  const { requestCloseMessageDialog } = useMessageDialog();
  const navigate = useNavigate();
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadingRef = useRef(false);

  const loadPage = useCallback(
    async (nextPage: number) => {
      if (loadingRef.current) {
        return;
      }
      loadingRef.current = true;
      try {
        const result = await fetchConversations({ page: nextPage, size: PAGE_SIZE }).unwrap();
        setConversations((prev) => (nextPage === 0 ? result.items : [...prev, ...result.items]));
        setHasMore(!result.last);
        setPage(nextPage);
      } finally {
        loadingRef.current = false;
      }
    },
    [fetchConversations],
  );

  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (!node || !hasMore) {
        return;
      }
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingRef.current) {
          loadPage(page + 1);
        }
      });
      observerRef.current.observe(node);
    },
    [hasMore, page, loadPage],
  );

  useEffect(() => {
    loadPage(0);
  }, [loadPage]);

  if (isLoading && conversations.length === 0) {
    return (
      <div className="space-y-2 px-1 py-2">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (!conversations.length) {
    return <p className="py-10 text-center text-sm text-muted-foreground">No conversations yet</p>;
  }

  return (
    <div className="max-h-[420px] overflow-y-auto">
      {conversations.map((conversation) => {
        const isUnread = conversation.unreadCount > 0;
        return (
          <button
            key={conversation.id}
            onClick={() => {
              navigate(`/inbox/${conversation.id}`);
              requestCloseMessageDialog();
            }}
            className="flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left transition-colors hover:bg-muted/50"
          >
            <Avatar className="h-9 w-9 shrink-0">
              <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">
                {getConversationInitials(conversation) ?? <User className="h-4 w-4" />}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className={cn("truncate text-[13px] font-semibold dark:text-white", isUnread && "font-bold")}>
                {getDisplayName(conversation)}
              </p>
              <p className={cn("truncate text-[11px] text-muted-foreground", isUnread && "font-semibold text-foreground")}>
                {conversation.lastMessagePreview || "No messages yet"}
              </p>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
              <span className="text-[11px] tabular-nums text-muted-foreground">
                {shortTimeAgo(conversation.lastMessageAt)}
              </span>
              {isUnread && (
                <span className="rounded-full bg-primary px-1.5 py-px text-[10px] font-bold text-primary-foreground tabular-nums">
                  {conversation.unreadCount}
                </span>
              )}
            </div>
          </button>
        );
      })}

      {hasMore && <div ref={sentinelRef} className="h-8" />}
      {isLoading && conversations.length > 0 && (
        <div className="flex justify-center py-3">
          <Skeleton className="h-8 w-8 rounded-full" />
        </div>
      )}
    </div>
  );
}
